import React from 'react';
import { 
    Drawer, Modal, Space, Typography, Button, Spin, Empty, Alert, 
    Popconfirm, theme, Tag, Tooltip
} from 'antd';
import { 
    DeleteOutlined, CloseOutlined, SaveOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../utils/dayjs';

const { Text } = Typography;

const EntityContainer = ({
    // Container configuration
    containerType = 'drawer', // 'drawer' | 'modal' | 'inline'
    
    // Common props
    mode = 'view',
    visible,
    onClose,
    title,
    content,
    loading = false,
    error,
    
    // Save functionality
    onSave,
    saving = false,
    hasChanges = false,
    saveDisabled = false,
    saveText,
    showSaveBar = false,
    
    // Delete functionality
    onDelete,
    enableDelete = false,
    deleteConfirmTitle,
    deleteConfirmDescription,
    
    // Layout options
    width = 900,
    height,
    extraActions,
    auditInfo,
    
    // Container-specific props
    drawerProps = {},
    modalProps = {},
    inlineProps = {}
}) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    const getTitle = () => {
        const baseTitle = title || t('Details');
        const modeTag = mode === 'create' 
            ? <Tag color="green">{t('Create')}</Tag>
            : mode === 'edit'
            ? <Tag color="blue">{t('Edit')}</Tag>
            : <Tag color="default">{t('View')}</Tag>;
        
        return (
            <Space size="middle">
                <Space>
                    {baseTitle}
                    {modeTag}
                </Space>
                {mode !== 'create' && auditInfo?.updatedAt && (
                    <Tooltip title={
                        <Space direction="vertical" size={2}>
                            {auditInfo.createdAt && (
                                <Text style={{ color: '#fff', fontSize: 11 }}>
                                    Created: {dayjs(auditInfo.createdAt).format('DD/MM/YYYY HH:mm')}
                                    {auditInfo.createdBy && ` by ${auditInfo.createdBy}`}
                                </Text>
                            )}
                            {auditInfo.updatedAt && (
                                <Text style={{ color: '#fff', fontSize: 11 }}>
                                    Updated: {dayjs(auditInfo.updatedAt).format('DD/MM/YYYY HH:mm')}
                                    {auditInfo.updatedBy && ` by ${auditInfo.updatedBy}`}
                                </Text>
                            )}
                        </Space>
                    }>
                        <Text type="secondary" style={{ fontSize: 11, cursor: 'help' }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {dayjs(auditInfo.updatedAt).format('DD/MM/YYYY HH:mm')}
                        </Text>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const getActions = () => (
        <Space size="small">
            {extraActions}
            {enableDelete && onDelete && (
                <Popconfirm
                    title={deleteConfirmTitle || t('Delete item?')}
                    description={deleteConfirmDescription || t('This action cannot be undone.')}
                    onConfirm={onDelete}
                    okText={t('Delete')}
                    cancelText={t('Cancel')}
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                    >
                        {t('Delete')}
                    </Button>
                </Popconfirm>
            )}
        </Space>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    minHeight: '200px'
                }}>
                    <Spin size="large" tip={t('Loading...')} />
                </div>
            );
        }
        
        if (error) {
            return (
                <Alert
                    type="error"
                    message={t('Error')}
                    description={Array.isArray(error) ? error.join(', ') : error}
                    closable
                />
            );
        }
        
        if (!content) {
            return (
                <Empty 
                    description={t('No content available')} 
                    style={{ marginTop: '60px' }}
                />
            );
        }
        
        return content;
    };

    const renderSaveBar = () => {
        if (!showSaveBar || loading) return null;
        
        return (
            <div style={{
                padding: '16px 24px',
                backgroundColor: token.colorBgElevated,
                borderTop: `1px solid ${token.colorBorder}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)'
            }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {hasChanges ? t('You have unsaved changes') : t('No changes')}
                </Text>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={onSave}
                    loading={saving}
                    disabled={saveDisabled || !hasChanges}
                    size="large"
                >
                    {saveText || t('Save')}
                </Button>
            </div>
        );
    };

    // Drawer container
    if (containerType === 'drawer') {
        return (
            <Drawer
                title={
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        {getTitle()}
                        {mode === 'view' && getActions()}
                    </div>
                }
                placement="right"
                width={width}
                open={visible}
                onClose={onClose}
                destroyOnClose
                closable={true}
                closeIcon={<CloseOutlined />}
                styles={{
                    body: {
                        padding: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
                {...drawerProps}
            >
                <div style={{ 
                    flex: 1, 
                    overflow: 'auto',
                    padding: '24px',
                    paddingBottom: showSaveBar ? '80px' : '24px'
                }}>
                    {renderContent()}
                </div>
                
                {showSaveBar && (
                    <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 10
                    }}>
                        {renderSaveBar()}
                    </div>
                )}
            </Drawer>
        );
    }

    // Modal container
    if (containerType === 'modal') {
        return (
            <Modal
                title={
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                    }}>
                        {getTitle()}
                        {mode === 'view' && getActions()}
                    </div>
                }
                open={visible}
                onCancel={onClose}
                width={width}
                style={{ height }}
                destroyOnClose
                footer={showSaveBar ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {hasChanges ? t('You have unsaved changes') : t('No changes')}
                        </Text>
                        <Space>
                            <Button onClick={onClose}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={onSave}
                                loading={saving}
                                disabled={saveDisabled || !hasChanges}
                            >
                                {saveText || t('Save')}
                            </Button>
                        </Space>
                    </div>
                ) : null}
                {...modalProps}
            >
                <div style={{ 
                    maxHeight: height ? `${height - 200}px` : '60vh',
                    overflow: 'auto'
                }}>
                    {renderContent()}
                </div>
            </Modal>
        );
    }

    // Inline container
    if (containerType === 'inline') {
        return (
            <div style={{ width: '100%', ...inlineProps.style }} {...inlineProps}>
                {/* Header */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 16,
                    padding: '16px',
                    backgroundColor: token.colorFillQuaternary,
                    borderRadius: token.borderRadius
                }}>
                    {getTitle()}
                    {mode === 'view' && getActions()}
                </div>
                
                {/* Content */}
                <div style={{ padding: '0 16px' }}>
                    {renderContent()}
                </div>
                
                {/* Save bar */}
                {showSaveBar && (
                    <div style={{ 
                        marginTop: 16,
                        padding: '16px',
                        backgroundColor: token.colorFillQuaternary,
                        borderRadius: token.borderRadius
                    }}>
                        {renderSaveBar()}
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default EntityContainer;
















